/*****************************************************************
 *  ZOHO BRIDGE PANEL — ONE RECORD CARD IN THE WAITING PILE        *
 *  File: apps/web/src/pages/bridge/InboxCard.js                   *
 *  What it does: shows one Zoho record: what Zoho says, what      *
 *  would change in Butler, who it looks like (contacts), and the  *
 *  Approve / Reject buttons with a "why" box.                     *
 *  Reads: nothing itself.  Writes: approve / reject through       *
 *  bridgeApi.js, only when a person confirms.                     *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — WHAT TO CALL THE RECORD                            *
 *  SECTION 2 — THE "WHAT ZOHO SAYS" LIST                          *
 *  SECTION 3 — THE "WHAT WOULD CHANGE" LIST                       *
 *  SECTION 4 — THE MATCH LINE (contacts)                          *
 *  SECTION 5 — THE CARD                                           *
 *****************************************************************/

import React, { useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, KIND_NAMES, MATCH_NAMES } from './bridgeWords';
import { StatusChip, ToneChip, WhyBox, formatWhen, formatMoney } from './bridgeParts';
import { approveRecord, rejectRecord } from './bridgeApi';

// The statuses a person can still decide on (same list as the server).
const DECIDABLE_STATUSES = ['proposed', 'failed'];

/*****************************************************************
 *  SECTION 1 — WHAT TO CALL THE RECORD                            *
 *****************************************************************/

/* ---- nameTheRecord(record) -- the card's title ---- */
function nameTheRecord(record) {
  const data = record.data || {};

  if (record.kind === 'books_item') {
    return data.name || 'Unnamed price';
  }

  const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');

  return fullName || data.accountName || data.email || 'Unnamed contact';
}

/*****************************************************************
 *  SECTION 2 — THE "WHAT ZOHO SAYS" LIST                          *
 *****************************************************************/

/* ---- listZohoFacts(record) -- [label, value] pairs worth showing ---- */
function listZohoFacts(record) {
  const data = record.data || {};

  if (record.kind === 'books_item') {
    return [
      ['Price', formatMoney(data.rate)],
      ['Unit', data.unit],
      ['Active?', data.status === 'inactive' ? 'No (inactive in Zoho)' : 'Yes'],
      ['Type', data.productType],
      ['Taxable?', data.taxable ? 'Yes' : 'No'],
      ['Description', data.description],
    ].filter((onePair) => Boolean(onePair[1]));
  }

  const streetLine = [data.street, data.city, data.state, data.zip].filter(Boolean).join(', ');

  return [
    ['Email', data.email],
    ['Phone', data.phone],
    ['Mobile', data.mobile],
    ['Address', streetLine],
    ['Account', data.accountName],
  ].filter((onePair) => Boolean(onePair[1]));
}

/* ---- FactList({ facts }) -- a two-column list ---- */
function FactList({ facts }) {
  return (
    <dl className="zb-facts">
      {facts.map((onePair) => (
        <div className="zb-fact" key={onePair[0]}>
          <dt>{onePair[0]}</dt>
          <dd>{onePair[1]}</dd>
        </div>
      ))}
    </dl>
  );
}

/*****************************************************************
 *  SECTION 3 — THE "WHAT WOULD CHANGE" LIST                       *
 *****************************************************************/

/* ---- ChangeList({ record }) -- before -> after, one line each ---- */
function ChangeList({ record }) {
  const diff = record.diff || {};
  const changes = Array.isArray(diff.changes) ? diff.changes : [];
  const isDone = record.status === 'applied';
  const heading = isDone ? BRIDGE_WORDS.whatChanged : BRIDGE_WORDS.whatChanges;

  return (
    <div className="zb-block">
      <h4 className="zb-subhead">{heading}</h4>
      {diff.isNew ? <p className="zb-quiet">{isDone ? BRIDGE_WORDS.wasBrandNew : BRIDGE_WORDS.brandNew}</p> : null}
      {diff.note ? <p className="zb-quiet">{diff.note}</p> : null}
      {changes.length === 0 && !diff.isNew ? <p className="zb-quiet">{BRIDGE_WORDS.noChanges}</p> : null}
      {changes.length > 0 ? (
        <ul className="zb-changes">
          {changes.map((oneChange) => (
            <li key={oneChange.field}>
              <strong>{oneChange.field}</strong>
              <span className="zb-before">{oneChange.before || '(blank)'}</span>
              <span aria-hidden="true"> → </span>
              <span className="zb-visually-hidden"> becomes </span>
              <span className="zb-after">{oneChange.after || '(blank)'}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 — THE MATCH LINE (contacts)                          *
 *****************************************************************/

/* ---- MatchLine({ record }) -- who this contact looks like ---- */
function MatchLine({ record }) {
  if (record.kind !== 'crm_contact') {
    return null;
  }

  const hint = record.matchHint || {};
  const matchedCustomer = record.matchedCustomer;

  if (hint.how === 'ambiguous') {
    return (
      <div className="zb-notice zb-notice-warn">
        <BridgeIcon name="alert" />
        <span>{hint.reason || MATCH_NAMES.ambiguous}</span>
      </div>
    );
  }

  if (!matchedCustomer) {
    return <p className="zb-quiet">{BRIDGE_WORDS.noMatch}</p>;
  }

  const matchWord = MATCH_NAMES[hint.how] || hint.how || '';
  const isMedium = hint.confidence === 'medium';
  const isDone = record.status === 'applied';

  // honest UI: once applied, the card says what IS linked, not what it looks like
  if (isDone) {
    return (
      <div className="zb-notice zb-notice-ok">
        <BridgeIcon name="link" />
        <span>{BRIDGE_WORDS.linkedToCustomer} <strong>{matchedCustomer.displayName}</strong></span>
      </div>
    );
  }

  return (
    <div className={'zb-notice ' + (isMedium ? 'zb-notice-warn' : 'zb-notice-info')}>
      <BridgeIcon name="people" />
      <span>
        {BRIDGE_WORDS.looksLike} <strong>{matchedCustomer.displayName}</strong>
        {matchWord ? ' — ' + BRIDGE_WORDS.matchedBy + ' ' + matchWord : ''}
      </span>
    </div>
  );
}

/*****************************************************************
 *  SECTION 5 — THE CARD                                           *
 *****************************************************************/

/* ---- InboxCard({ record, isSelected, onToggleSelect, onDecided })
   One record. onDecided() is called after any decision so the
   tab can reload the pile and the counts.                       */
export default function InboxCard({ record, isSelected, onToggleSelect, onDecided }) {
  const [openBox, setOpenBox] = useState('');          // '', 'approve' or 'reject'
  const [isBusy, setIsBusy] = useState(false);          // a call is on its way
  const [answerText, setAnswerText] = useState('');     // what the server said
  const [answerTone, setAnswerTone] = useState('info'); // how to colour it
  const [useOlder, setUseOlder] = useState(false);      // owner override
  const [needsOlderTick, setNeedsOlderTick] = useState(false);

  const canDecide = DECIDABLE_STATUSES.includes(record.status);
  const kindWord = KIND_NAMES[record.kind] || record.kind;
  const kindIcon = record.kind === 'books_item' ? 'tag' : 'people';
  const checkboxId = 'zb-select-' + record.id;

  /* ---- sendDecision(whyText) -- approve or reject, then report ---- */
  async function sendDecision(whyText) {
    setIsBusy(true);
    setAnswerText('');

    let answer = null;

    if (openBox === 'approve') {
      answer = await approveRecord(record.id, whyText, useOlder);
    } else {
      answer = await rejectRecord(record.id, whyText);
    }

    setIsBusy(false);

    // the server refused because a newer version is already in Butler
    if (answer.status === 409 && answer.message && answer.message.includes('older version')) {
      setNeedsOlderTick(true);
      setAnswerTone('warn');
      setAnswerText(answer.message);
      return;
    }

    if (!answer.ok) {
      setAnswerTone('bad');
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    // honest UI: say exactly what the server says happened
    const outcome = answer.data ? answer.data.outcome : '';

    if (outcome === 'failed') {
      setAnswerTone('bad');
      setAnswerText(BRIDGE_WORDS.failedBecause + ' ' + answer.data.message);
    }

    setOpenBox('');
    onDecided();
  }

  const olderTickBox = needsOlderTick ? (
    <label className="zb-check">
      <input type="checkbox" checked={useOlder} onChange={(event) => setUseOlder(event.target.checked)} />
      <span>{BRIDGE_WORDS.useOlderAnyway}</span>
    </label>
  ) : null;

  return (
    <article className="zb-card" aria-labelledby={'zb-title-' + record.id}>
      <header className="zb-card-head">
        {canDecide ? (
          <label className="zb-check zb-check-only" htmlFor={checkboxId}>
            <input
              id={checkboxId}
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(record.id)}
            />
            <span className="zb-visually-hidden">Select {nameTheRecord(record)}</span>
          </label>
        ) : null}
        <div className="zb-card-titles">
          <h3 className="zb-card-title" id={'zb-title-' + record.id}>{nameTheRecord(record)}</h3>
          <div className="zb-row zb-row-tight">
            <ToneChip tone="neutral" icon={kindIcon} word={kindWord} />
            <StatusChip status={record.status} />
          </div>
        </div>
      </header>

      <div className="zb-card-body">
        <div className="zb-block">
          <h4 className="zb-subhead">{BRIDGE_WORDS.whatZohoSays}</h4>
          <FactList facts={listZohoFacts(record)} />
        </div>
        <ChangeList record={record} />
        <MatchLine record={record} />

        {record.status === 'failed' && record.failureReason ? (
          <div className="zb-notice zb-notice-bad">
            <BridgeIcon name="alert" />
            <span>{BRIDGE_WORDS.failedBecause} {record.failureReason}</span>
          </div>
        ) : null}

        <p className="zb-small">
          {BRIDGE_WORDS.receivedAt} {formatWhen(record.receivedAt)}
          {record.decidedAt ? ' · ' + BRIDGE_WORDS.decidedBy + ' ' + formatWhen(record.decidedAt) : ''}
          {record.decisionWhy ? ' — "' + record.decisionWhy + '"' : ''}
        </p>
      </div>

      {answerText ? (
        <div className={'zb-notice zb-notice-' + answerTone} role="status">
          <span>{answerText}</span>
        </div>
      ) : null}

      {canDecide && openBox === '' ? (
        <div className="zb-row">
          <button type="button" className="zb-button zb-button-primary" onClick={() => setOpenBox('approve')}>
            <BridgeIcon name="check" />
            <span>{BRIDGE_WORDS.approve}</span>
          </button>
          <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenBox('reject')}>
            <BridgeIcon name="cross" />
            <span>{BRIDGE_WORDS.reject}</span>
          </button>
        </div>
      ) : null}

      {canDecide && openBox !== '' ? (
        <WhyBox
          confirmWord={openBox === 'approve' ? BRIDGE_WORDS.confirmApprove : BRIDGE_WORDS.confirmReject}
          tone={openBox === 'approve' ? 'primary' : 'danger'}
          busy={isBusy}
          extra={openBox === 'approve' ? olderTickBox : null}
          onConfirm={sendDecision}
          onCancel={() => setOpenBox('')}
        />
      ) : null}
    </article>
  );
}
